import { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  BarChart3, Music, Upload, Zap, CheckCircle, AlertCircle,
  Clock, TrendingDown, MessageSquare, Download, Cpu, Layers,
  HardDrive, RefreshCw,
} from 'lucide-react';
import { API } from '@/App';

const ACTION_META = {
  upload:            { label: 'Uploads',          color: '#2563FF', icon: Upload },
  analyze:           { label: 'Analysis',          color: '#14E6C9', icon: Cpu },
  chat_message:      { label: 'DJ Coach chats',    color: '#8B5CF6', icon: MessageSquare },
  download_m3u8:     { label: 'M3U8 exports',      color: '#F59E0B', icon: Download },
  download_mp3:      { label: 'MP3 downloads',     color: '#EF4444', icon: Download },
  ai_set_generation: { label: 'AI set generation', color: '#10B981', icon: Layers },
};

function StatCard({ icon: Icon, label, value, sub, color = '#2563FF' }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white border border-[#E2E8F0] rounded-2xl p-5"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${color}18` }}>
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
      </div>
      <p className="font-heading text-2xl font-bold text-[#0F172A]">{value}</p>
      <p className="font-body text-sm text-[#64748B] mt-0.5">{label}</p>
      {sub && <p className="font-mono text-[10px] text-[#94A3B8] mt-1">{sub}</p>}
    </motion.div>
  );
}

export default function AnalyticsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const r = await axios.get(`${API}/analytics/usage`);
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <RefreshCw className="w-6 h-6 text-[#2563FF] animate-spin" />
    </div>
  );

  if (error) return (
    <div className="p-8 text-center text-red-500 font-body">{error}</div>
  );

  const { credits, library, analysis_jobs, recent_transactions } = data;

  // Build spend breakdown bars
  const spendEntries = Object.entries(credits.spend_by_action || {})
    .filter(([, v]) => v > 0)
    .sort(([, a], [, b]) => b - a);
  const maxSpend = spendEntries[0]?.[1] || 1;

  // Daily spend chart
  const dailyData = credits.daily_spend || [];
  const maxDaily  = Math.max(...dailyData.map(d => d.credits), 1);

  return (
    <div className="p-4 sm:p-8 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#0F172A]">Analytics</h1>
          <p className="font-body text-sm text-[#64748B] mt-1">Your API usage and library overview</p>
        </div>
        <button
          onClick={load}
          className="p-2 rounded-xl border border-[#E2E8F0] bg-white hover:border-[#2563FF]/40 transition-colors"
          data-testid="analytics-refresh-btn"
        >
          <RefreshCw className="w-4 h-4 text-[#64748B]" />
        </button>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
        <StatCard icon={Music}        label="Total tracks"   value={library.total}          color="#2563FF" />
        <StatCard icon={CheckCircle}  label="Analyzed"       value={library.analyzed}        color="#10B981" sub={`${library.total ? Math.round(library.analyzed/library.total*100) : 0}% of library`} />
        <StatCard icon={Zap}          label="Credits spent"  value={credits.total_spent}     color="#EF4444" />
        <StatCard icon={BarChart3}    label="Analysis jobs"  value={analysis_jobs.completed} color="#8B5CF6" sub={analysis_jobs.failed > 0 ? `${analysis_jobs.failed} failed` : 'all successful'} />
      </div>

      {/* Library source breakdown */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5">
          <h3 className="font-heading text-sm text-[#0F172A] mb-4">Track sources</h3>
          <div className="space-y-3">
            {[
              { label: 'Web uploads',     value: library.web_uploads,    color: '#2563FF' },
              { label: 'Other sources',    value: library.desktop_synced, color: '#14E6C9' },
              { label: 'Not analyzed',    value: library.unanalyzed,     color: '#F59E0B' },
            ].map(({ label, value, color }) => {
              const pct = library.total > 0 ? Math.round(value / library.total * 100) : 0;
              return (
                <div key={label}>
                  <div className="flex justify-between mb-1">
                    <span className="font-body text-xs text-[#64748B]">{label}</span>
                    <span className="font-mono text-xs text-[#0F172A]">{value} <span className="text-[#94A3B8]">({pct}%)</span></span>
                  </div>
                  <div className="h-1.5 bg-[#F5F7FB] rounded-full overflow-hidden">
                    <motion.div
                      className="h-full rounded-full"
                      style={{ background: color }}
                      initial={{ width: 0 }}
                      animate={{ width: `${pct}%` }}
                      transition={{ duration: 0.6, delay: 0.1 }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5">
          <h3 className="font-heading text-sm text-[#0F172A] mb-4">Analysis queue</h3>
          <div className="space-y-3">
            {[
              { label: 'Completed',     value: analysis_jobs.completed, color: '#10B981' },
              { label: 'Failed',        value: analysis_jobs.failed,    color: '#EF4444' },
              { label: 'In progress',   value: analysis_jobs.pending,   color: '#F59E0B' },
            ].map(({ label, value, color }) => (
              <div key={label} className="flex items-center justify-between px-3 py-2 bg-[#F5F7FB] rounded-lg">
                <span className="font-body text-xs text-[#64748B]">{label}</span>
                <span className="font-mono text-sm font-bold" style={{ color }}>{value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Credit spend breakdown */}
      {spendEntries.length > 0 && (
        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5 mb-8">
          <h3 className="font-heading text-sm text-[#0F172A] mb-4">Credit usage by action</h3>
          <div className="space-y-3">
            {spendEntries.map(([action, amount]) => {
              const meta = ACTION_META[action] || { label: action, color: '#94A3B8', icon: Zap };
              const Icon = meta.icon;
              const pct  = Math.round((amount / maxSpend) * 100);
              return (
                <div key={action} className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${meta.color}18` }}>
                    <Icon className="w-3.5 h-3.5" style={{ color: meta.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between mb-1">
                      <span className="font-body text-xs text-[#64748B]">{meta.label}</span>
                      <span className="font-mono text-xs font-bold text-[#0F172A]">{amount} cr</span>
                    </div>
                    <div className="h-1.5 bg-[#F5F7FB] rounded-full overflow-hidden">
                      <motion.div
                        className="h-full rounded-full"
                        style={{ background: meta.color }}
                        initial={{ width: 0 }}
                        animate={{ width: `${pct}%` }}
                        transition={{ duration: 0.5 }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-4 pt-4 border-t border-[#E2E8F0] flex items-center justify-between">
            <span className="font-body text-sm text-[#64748B]">Total credits spent</span>
            <span className="font-heading text-base font-bold text-[#EF4444]">{credits.total_spent} cr</span>
          </div>
        </div>
      )}

      {/* Daily credit spend chart */}
      {dailyData.length > 1 && (
        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5 mb-8">
          <h3 className="font-heading text-sm text-[#0F172A] mb-4">Daily credit spend (last 30 days)</h3>
          <div className="flex items-end gap-1 h-24">
            {dailyData.map((d, i) => {
              const h = Math.max(4, Math.round((d.credits / maxDaily) * 96));
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-1 group relative">
                  <div
                    className="w-full bg-[#2563FF]/20 hover:bg-[#2563FF]/40 rounded-sm transition-colors cursor-default"
                    style={{ height: `${h}px` }}
                  />
                  <div className="absolute -top-6 left-1/2 -translate-x-1/2 hidden group-hover:block bg-[#0F172A] text-white text-[9px] font-mono px-1.5 py-0.5 rounded whitespace-nowrap z-10">
                    {d.date}<br />{d.credits} cr
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex justify-between mt-1">
            <span className="font-mono text-[9px] text-[#94A3B8]">{dailyData[0]?.date}</span>
            <span className="font-mono text-[9px] text-[#94A3B8]">{dailyData[dailyData.length-1]?.date}</span>
          </div>
        </div>
      )}

      {/* Recent transactions */}
      {recent_transactions?.length > 0 && (
        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5">
          <h3 className="font-heading text-sm text-[#0F172A] mb-4">Recent API activity</h3>
          <div className="space-y-1.5">
            {recent_transactions.map(tx => {
              const meta = ACTION_META[tx.action] || { label: tx.action, color: '#94A3B8', icon: Zap };
              const Icon = meta.icon;
              return (
                <div key={tx.id} className="flex items-center gap-3 px-3 py-2 bg-[#F5F7FB] rounded-lg" data-testid={`txn-row-${tx.id}`}>
                  <Icon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: meta.color }} />
                  <span className="font-body text-xs text-[#64748B] flex-1 truncate">
                    {meta.label}
                    {tx.description ? ` — ${tx.description}` : ''}
                  </span>
                  <span className={`font-mono text-xs font-bold ${tx.type === 'credit' ? 'text-emerald-600' : 'text-red-500'}`}>
                    {tx.type === 'credit' ? '+' : ''}{tx.amount} cr
                  </span>
                  <span className="font-mono text-[10px] text-[#94A3B8] hidden sm:inline">
                    {new Date(tx.created_at).toLocaleDateString()}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Empty state */}
      {!spendEntries.length && !recent_transactions?.length && (
        <div className="text-center py-16">
          <BarChart3 className="w-12 h-12 text-[#E2E8F0] mx-auto mb-4" />
          <p className="font-heading text-[#0F172A]">No activity yet</p>
          <p className="font-body text-sm text-[#94A3B8] mt-1">Upload and analyze tracks to see your usage here.</p>
        </div>
      )}
    </div>
  );
}
